import { FileText, Truck, Download, Shield, CheckCircle2, ArrowRight, Package } from "lucide-react";
import { FeaturePageLayout } from "../FeaturePageLayout";
import { PageMeta } from "@/components/seo/PageMeta";
import { useAuthDialog } from "@/lib/auth-dialog-context";

const CARRIERS = [
  "Delhivery", "Ekart", "Xpressbees", "Blue Dart", "Shadowfax",
  "Ecom Express", "Valmo", "DTDC", "Smartr", "India Post",
];

const BENEFITS = [
  {
    icon: Truck,
    title: "Auto-detects 10 couriers",
    desc: "Valmo, Blue Dart, Delhivery, Ekart, Xpressbees, Shadowfax, Ecom Express, DTDC, Smartr, India Post — all identified automatically.",
  },
  {
    icon: Download,
    title: "Download by courier or all at once",
    desc: "Get a separate PDF per courier, or download a single ZIP with every courier's file bundled.",
  },
  {
    icon: Shield,
    title: "100% in-browser — nothing uploaded",
    desc: "Your shipping labels never leave your device. All PDF processing runs locally in the browser.",
  },
  {
    icon: FileText,
    title: "Order statistics at a glance",
    desc: "Instantly see total orders, prepaid count, and COD count from the uploaded PDF.",
  },
];

export const LabelDeskLandingPage = () => {
  const { openAuth } = useAuthDialog();

  return (
    <FeaturePageLayout currentPath="/labeldesk">
      <PageMeta
        title="LabelDesk — Split & Sort Meesho Shipping Labels by Courier"
        description="Upload your Meesho bulk label PDF and get it auto-sorted by courier (Delhivery, Ekart, Xpressbees & more), 100% in-browser. Free, no upload to any server."
        keywords="meesho label splitter, meesho shipping label pdf, split meesho labels by courier, delhivery label download, ekart label pdf, meesho label sorter"
        path="/labeldesk"
        ogTitle="LabelDesk — Split Meesho Shipping Labels by Courier | EasyLister"
        ogDescription="Upload your Meesho bulk label PDF and get it auto-sorted by courier. 10 carriers detected automatically. 100% in-browser, no uploads."
        twitterTitle="LabelDesk — Auto-sort Meesho shipping labels by courier"
        twitterDescription="Upload once, get per-courier PDFs instantly. Supports Delhivery, Ekart, Xpressbees & 7 more. 100% in-browser."
      />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-6">
              EasyLister · LabelDesk
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Split & Sort Meesho{" "}
              <span className="bg-gradient-to-br from-purple-700 to-violet-600 bg-clip-text text-transparent">
                Shipping Labels
              </span>{" "}
              by Courier
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Upload your Meesho bulk label PDF and EasyLister automatically identifies which pages
              belong to which courier — Delhivery, Ekart, Xpressbees, and 7 more. Download a
              separate PDF per courier or grab everything in one ZIP. All processing happens in your
              browser; your labels are never uploaded anywhere.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => openAuth("signup")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-full shadow-md hover:shadow-lg transition-all text-sm"
              >
                Try Free — No Card Needed <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Courier list */}
      <section className="py-14 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">
            Supported Couriers — Auto-detected
          </p>
          <div className="flex flex-wrap gap-3">
            {CARRIERS.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-purple-100 text-sm font-semibold text-slate-700 shadow-sm"
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-12">
            Everything you need to hand off orders to couriers faster
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6">
            {BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="p-6 rounded-2xl border border-slate-100 bg-slate-50 hover:border-purple-200 hover:bg-purple-50/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-gradient-to-b from-white to-purple-50/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-10">
            How it works
          </h2>
          <ol className="space-y-6">
            {[
              "Upload your Meesho bulk shipping label PDF",
              "LabelDesk scans each page and identifies the courier from the label design",
              "View a summary — total orders, prepaid, COD, and per-courier breakdown",
              "Download individual PDFs per courier, or a single ZIP with all of them",
            ].map((step, i) => (
              <li key={i} className="flex gap-4 items-start">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white text-sm font-extrabold flex items-center justify-center">
                  {i + 1}
                </span>
                <p className="text-slate-700 text-base leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </FeaturePageLayout>
  );
};
